import React from 'react';
import { Check, Layers } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderPill from '@/components/editorial/AnimatedBorderPill';
import storilyIcon from '@/assets/storily-icon.png';
import storilyIconDark from '@/assets/storily-icon-dark.svg';

const EditorialStatsBar: React.FC = () => {
  const { theme } = useTheme();

  // Theme-aware colors
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const borderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const cardBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const altRowBgColor = theme === 'dark' ? '#222222' : '#FAFAFA';

  const features = [
    { name: 'CRM & Pipeline Management', replaces: 'HubSpot & Others', cost: '$997/monthly' },
    { name: 'AI Agents', replaces: 'Sintra & Others', cost: '$97/monthly' },
    { name: 'Unlimited Sales Funnels', replaces: 'ClickFunnels & Others', cost: '$297/monthly' },
    { name: 'Website Builder', replaces: 'WordPress, Wix & Others', cost: '$29/monthly' },
    { name: 'Surveys & Forms', replaces: 'SurveyMonkey & Others', cost: '$49/monthly' },
    { name: 'Email Marketing', replaces: 'Mailchimp & Others', cost: '$99/monthly' },
    { name: '2-Way SMS Marketing', replaces: 'Twilio & Others', cost: '$99/monthly' },
    { name: 'Booking & Appointments', replaces: 'Calendly & Others', cost: '$29/monthly' },
    { name: 'Workflow Automations', replaces: 'Zapier & Others', cost: '$169/monthly' },
    { name: 'Courses/Products', replaces: 'Kajabi & Others', cost: '$99/monthly' },
    { name: 'Call Tracking', replaces: 'CallRail & Others', cost: '$49/monthly' },
    { name: 'Reputation Management', replaces: 'Birdeye & Others', cost: '$159/monthly' },
    { name: 'Tracking & Analytics', replaces: 'Google Analytics Pro', cost: '$299/monthly' },
    { name: 'Communities', replaces: 'Circle & Others', cost: '$89/monthly' },
    { name: 'Document Signing', replaces: 'DocuSign & Others', cost: '$47/monthly' },
    { name: 'Gray-Labeled Mobile App', replaces: 'Unique to Story', cost: '-' },
  ];

  return (
    <section className="py-16 transition-colors duration-300">
      <div className="max-w-[960px] mx-auto px-[7%] sm:px-8 lg:px-8">
        {/* Consolidate Badge */}
        <div className="flex justify-center pt-2 pb-6">
          <AnimatedBorderPill text="Consolidate" icon={Layers} />
        </div>


        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-2" style={{ color: headingColor }}>
            Kill the Frankenstein.
          </h2>
          <p className="text-sm" style={{ color: textColor }}>
            <span className="font-semibold" style={{ color: headingColor }}>Save $2,411/month</span> and get everything you need in one place.
          </p>
        </div>

        {/* Bento Card Container */}
        <div 
          className="rounded-2xl overflow-hidden"
          style={{ 
            backgroundColor: cardBgColor,
            border: `1px solid ${borderColor}`,
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 12px 24px -8px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.02)'
          }}
        >
          {/* Mobile Card Layout */}
          <div className="block md:hidden">
            <div className="p-4 space-y-3">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 py-3"
                  style={{ 
                    borderBottom: index === features.length - 1 ? 'none' : `1px solid ${borderColor}`,
                  }}
                >
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111' }}
                  >
                    <Check 
                      className="w-3 h-3" 
                      strokeWidth={3} 
                      style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium" style={{ color: headingColor }}>
                      {feature.name}
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: textColor }}>
                      Replaces: {feature.replaces}
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: textColor }}>
                      Typical Cost: {feature.cost}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {/* Mobile Footer */}
            <div 
              className="p-4 flex justify-between items-center"
              style={{ backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111' }}
            >
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}>
                  Without Storily
                </p>
                <p className="text-sm font-bold" style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}>
                  $2,608<span className="text-[10px] opacity-70">/mo</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <img src={theme === 'dark' ? storilyIconDark : storilyIcon} alt="Storily" width={20} height={20} className="h-5 w-auto invert" />
                <div className="text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}>
                    With Storily
                  </p>
                  <p className="text-sm font-bold" style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}>
                    $197<span className="text-[10px] opacity-70">/mo</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider" style={{ color: textColor }}>
                    Features
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider" style={{ color: textColor }}>
                    Replaces
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider" style={{ color: textColor }}>
                    Typical Cost
                  </th>
                  <th className="text-center py-4 px-6">
                    <div className="flex justify-center">
                      <img src={theme === 'dark' ? storilyIconDark : storilyIcon} alt="Storily" width={28} height={28} className="h-7 w-auto" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr 
                    key={index} 
                    style={{ 
                      borderBottom: index === features.length - 1 ? 'none' : `1px solid ${borderColor}`,
                      backgroundColor: index % 2 === 0 ? cardBgColor : altRowBgColor
                    }}
                  >
                    <td className="py-3 px-6 text-xs font-medium" style={{ color: headingColor }}>
                      {feature.name}
                    </td>
                    <td className="py-3 px-6 text-xs" style={{ color: textColor }}>
                      {feature.replaces}
                    </td>
                    <td className="py-3 px-6 text-xs" style={{ color: textColor }}>
                      {feature.cost}
                    </td>
                    <td className="py-3.5 px-6 text-center">
                      <div className="flex justify-center">
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111' }}
                        >
                          <Check 
                            className="w-3 h-3" 
                            strokeWidth={3} 
                            style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111' }}>
                  <td className="py-3 px-6 text-xs font-semibold uppercase tracking-wider" style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}>
                    Overall Price
                  </td>
                  <td className="py-3 px-6"></td>
                  <td className="py-3 px-6">
                    <div style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}>
                      <span className="text-sm font-bold">$2,608</span>
                      <span className="text-[10px] uppercase tracking-wider opacity-70 ml-1">/month</span>
                    </div>
                  </td>
                  <td className="py-3 px-6 text-center">
                    <div style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}>
                      <span className="text-sm font-bold">$197</span>
                      <span className="text-[10px] uppercase tracking-wider opacity-70 ml-1">/month</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        
        {/* Savings Text */}
        <p className="text-center mt-6 text-xs" style={{ color: textColor }}>
          One Platform. Zero Bloat. Infinite Scale. Streamline your stack. Slash your overheads.
        </p>
      </div>
    </section>
  );
};

export default EditorialStatsBar;
